import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface Sale {
  id: string;
  tenant_id: string;
  user_id: string;
  customer_id: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  invoice_number: string | null;
  total_amount: number;
  total_discount: number;
  received_amount: number;
  due_amount: number;
  cash_amount: number;
  online_amount: number;
  total_profit: number;
  payment_mode: string;
  payment_status: string;
  notes: string | null;
  is_deleted: boolean;
  deleted_at: string | null;
  deleted_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface SaleItem {
  id: string;
  sale_id: string;
  service_id: string | null;
  service_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  cost_price: number;
  profit: number;
  discount_amount: number;
  original_price: number;
  created_at: string;
}

export function useSales() {
  const { user, tenant } = useAuth();

  const {
    data: sales,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["sales", tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return [];

      const { data, error } = await supabase
        .from("sales")
        .select("*")
        .eq("tenant_id", tenant.id)
        .eq("is_deleted", false)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Sale[];
    },
    enabled: !!user && !!tenant?.id,
  });

  const createSale = async (
    saleData: Omit<Sale, "id" | "created_at" | "updated_at">,
    items: Omit<SaleItem, "id" | "sale_id" | "created_at">[]
  ) => {
    // Generate invoice number
    const invoiceNumber = `INV-${Date.now().toString(36).toUpperCase()}`;

    const { data: sale, error: saleError } = await supabase
      .from("sales")
      .insert({
        ...saleData,
        invoice_number: invoiceNumber,
      })
      .select()
      .single();

    if (saleError) throw saleError;

    // Insert sale items
    const saleItems = items.map((item) => ({
      ...item,
      sale_id: sale.id,
    }));

    const { error: itemsError } = await supabase
      .from("sale_items")
      .insert(saleItems);

    if (itemsError) throw itemsError;

    return sale;
  };

  const getSaleItems = async (saleId: string) => {
    const { data, error } = await supabase
      .from("sale_items")
      .select("*")
      .eq("sale_id", saleId);

    if (error) throw error;
    return data as SaleItem[];
  };

  return {
    sales,
    isLoading,
    error,
    refetch,
    createSale,
    getSaleItems,
  };
}
